import { Users, Banknote, Building2, Bus, GraduationCap, Shield, BookOpen, Map, Compass, TrendingUp, Leaf, Calculator, Rocket, Megaphone, UtensilsCrossed, FolderHeart, Briefcase, Atom, Car, type LucideIcon } from 'lucide-react';

export interface KanbanColumn {
  value: string;
  label: string;
  color: string;
}

export interface ProgramConfig {
  id: string;
  serviceType: string;
  label: string;
  shortLabel: string;
  icon: LucideIcon;
  color: string;
  gradient: string;
  routeBase: string;
  applyRoute: string;
  kanbanStatusField: string;
  kanbanColumns: KanbanColumn[];
  features: {
    ai: boolean;
    documents: boolean;
    contractAcceptance: boolean;
    clientPortal: boolean;
    meetings: boolean;
    pointsSystem: boolean;
    certificates: boolean;
    courses: boolean;
    purchaseList: boolean;
    loanInfo: boolean;
    repaymentSchedule: boolean;
    businessPlan: boolean;
    operatorApplication: boolean;
  };
  enabled: boolean;
  comingSoon?: boolean;
  terminology?: {
    singular: string;       // "rezerwacja" / "wniosek"
    plural: string;         // "rezerwacji" / "wniosków"
    newItem: string;        // "Nowa rezerwacja" / "Nowy wniosek"
    pipeline: string;       // "Pipeline — etapy obsługi" / "Pipeline — konwersja etapów"
    winLabel: string;       // "Zrealizowane" / "Win rate"
    archiveEmpty: string;   // "Brak zarchiwizowanych rezerwacji"
    archiveHint: string;    // "Rezerwacje trafiają tu po zakończeniu..."
    yearLabel: string;      // "sezon" / "nabór"
  };
}

// ══════════════════════════════════
// PUP Grant (Dotacje PUP)
// ══════════════════════════════════
const PUP_STATUSES: KanbanColumn[] = [
  { value: 'nowe_zgloszenie', label: 'Nowe zgłoszenie', color: 'bg-blue-500' },
  { value: 'weryfikacja_ok', label: 'Weryfikacja OK', color: 'bg-green-500' },
  { value: 'ostrzezenie', label: 'Ostrzeżenie', color: 'bg-yellow-500' },
  { value: 'gotowy_do_pisania', label: 'Gotowy do pisania', color: 'bg-purple-500' },
  { value: 'umowa_podpisana', label: 'Umowa podpisana', color: 'bg-indigo-500' },
  { value: 'wniosek_zlozony', label: 'Wniosek złożony', color: 'bg-cyan-500' },
  { value: 'sukces', label: 'Sukces', color: 'bg-emerald-500' },
  { value: 'odrzućony', label: 'Odrzucony', color: 'bg-red-500' },
];

// ══════════════════════════════════
// Wsparcie na Starcie (Pożyczki)
// ══════════════════════════════════
const LOAN_STATUSES: KanbanColumn[] = [
  { value: 'zapytanie', label: 'Zapytanie', color: 'bg-blue-500' },
  { value: 'kompletacja_dok', label: 'Kompletacja dok.', color: 'bg-yellow-500' },
  { value: 'wniosek_zlozony', label: 'Wniosek złożony', color: 'bg-indigo-500' },
  { value: 'w_ocenie', label: 'W ocenie', color: 'bg-purple-500' },
  { value: 'zatwierdzony', label: 'Zatwierdźony', color: 'bg-green-500' },
  { value: 'umowa_podpisana', label: 'Umowa podpisana', color: 'bg-teal-500' },
  { value: 'srodki_wyplacone', label: 'Środki wypłacone', color: 'bg-emerald-600' },
  { value: 'w_splacie', label: 'W trakcie spłaty', color: 'bg-cyan-500' },
  { value: 'zakonczony', label: 'Spłacona / Umorzona', color: 'bg-gray-500' },
  { value: 'odrzućony', label: 'Odrzucony', color: 'bg-red-500' },
];

// ══════════════════════════════════
// KRS (Company Registration) - future
// ══════════════════════════════════
const KRS_STATUSES: KanbanColumn[] = [
  { value: 'zapytanie', label: 'Zapytanie', color: 'bg-blue-500' },
  { value: 'kompletacja_dok', label: 'Kompletacja dok.', color: 'bg-yellow-500' },
  { value: 'przygotowanie_aktu', label: 'Przygotowanie aktu', color: 'bg-purple-500' },
  { value: 'podpisy_notarialne', label: 'Podpisy notarialne', color: 'bg-indigo-500' },
  { value: 'zlozony_w_sadzie', label: 'Złożony w sądzie', color: 'bg-cyan-500' },
  { value: 'w_rejestracji', label: 'W rejestracji', color: 'bg-teal-500' },
  { value: 'zarejestrowany', label: 'Zarejestrowany', color: 'bg-emerald-500' },
  { value: 'odrzućony', label: 'Odrzucony', color: 'bg-red-500' },
];

// ══════════════════════════════════
// Turnusy (Trips/Events) - future
// ══════════════════════════════════
const TRIP_STATUSES: KanbanColumn[] = [
  { value: 'planowany', label: 'Planowany', color: 'bg-blue-500' },
  { value: 'otwarty', label: 'Otwarty nabór', color: 'bg-green-500' },
  { value: 'pelny', label: 'Komplet', color: 'bg-yellow-500' },
  { value: 'potwierdźony', label: 'Potwierdzony', color: 'bg-indigo-500' },
  { value: 'w_trakcie', label: 'W trakcie', color: 'bg-purple-500' },
  { value: 'zakonczony', label: 'Zakończony', color: 'bg-gray-500' },
  { value: 'anulowany', label: 'Odwołany', color: 'bg-red-500' },
];

// ══════════════════════════════════
// KFS (Krajowy Fundusz Szkoleniowy)
// ══════════════════════════════════
const KFS_STATUSES: KanbanColumn[] = [
  { value: 'zapytanie', label: 'Zapytanie', color: 'bg-blue-500' },
  { value: 'analiza', label: 'Analiza potrzeb', color: 'bg-yellow-500' },
  { value: 'wniosek_zlozony', label: 'Wniosek złożony', color: 'bg-indigo-500' },
  { value: 'zatwierdzony', label: 'Zatwierdźony', color: 'bg-green-500' },
  { value: 'szkolenie_w_toku', label: 'Szkolenie w toku', color: 'bg-purple-500' },
  { value: 'zakonczony', label: 'Zakończone', color: 'bg-emerald-500' },
  { value: 'odrzućony', label: 'Odrzucony', color: 'bg-red-500' },
];

// ══════════════════════════════════
// ZUS (Ulgi ZUS)
// ══════════════════════════════════
const ZUS_STATUSES: KanbanColumn[] = [
  { value: 'zapytanie', label: 'Zapytanie', color: 'bg-blue-500' },
  { value: 'analiza', label: 'Analiza sytuacji', color: 'bg-yellow-500' },
  { value: 'wniosek_zlozony', label: 'Wniosek złożony', color: 'bg-indigo-500' },
  { value: 'w_rozpatrywaniu', label: 'W rozpatrywaniu', color: 'bg-purple-500' },
  { value: 'zakonczony', label: 'Zakończone', color: 'bg-emerald-500' },
  { value: 'odrzućony', label: 'Odrzucone', color: 'bg-red-500' },
];

// ══════════════════════════════════
// BHP (Szkolenia BHP)
// ══════════════════════════════════
const BHP_STATUSES: KanbanColumn[] = [
  { value: 'zapytanie', label: 'Zapytanie', color: 'bg-blue-500' },
  { value: 'ustalanie_terminu', label: 'Ustalanie terminu', color: 'bg-yellow-500' },
  { value: 'zaplanowany', label: 'Zaplanowane', color: 'bg-indigo-500' },
  { value: 'w_toku', label: 'Szkolenie w toku', color: 'bg-purple-500' },
  { value: 'zakonczony', label: 'Zakończone', color: 'bg-emerald-500' },
  { value: 'anulowany', label: 'Anulowane', color: 'bg-red-500' },
];

// ══════════════════════════════════
// Szkolenia (ogólne)
// ══════════════════════════════════
const TRAINING_STATUSES: KanbanColumn[] = [
  { value: 'zapytanie', label: 'Zapytanie', color: 'bg-blue-500' },
  { value: 'analiza_potrzeb', label: 'Analiza potrzeb', color: 'bg-yellow-500' },
  { value: 'oferta_wyslana', label: 'Oferta wysłana', color: 'bg-cyan-500' },
  { value: 'zaplanowany', label: 'Zaplanowane', color: 'bg-indigo-500' },
  { value: 'w_toku', label: 'Szkolenie w toku', color: 'bg-purple-500' },
  { value: 'zakonczony', label: 'Zakończone', color: 'bg-emerald-500' },
  { value: 'anulowany', label: 'Anulowane', color: 'bg-red-500' },
];

// ══════════════════════════════════
// Wycieczki (EnhaTrip)
// ══════════════════════════════════
const EXCURSION_STATUSES: KanbanColumn[] = [
  { value: 'zapytanie', label: 'Zapytanie', color: 'bg-blue-500' },
  { value: 'rezerwacja', label: 'Rezerwacja', color: 'bg-yellow-500' },
  // FIX 2026-07-30: było 'potwierdźony' (z ź) — taka wartość NIE istnieje w bazie
  // (kanoniczna to 'potwierdzony'), więc potwierdzone rezerwacje nie pokazywały się w Kanbanie.
  { value: 'potwierdzony', label: 'Potwierdzona', color: 'bg-green-500' },
  { value: 'w_trakcie', label: 'W trakcie', color: 'bg-purple-500' },
  { value: 'zakonczony', label: 'Zakończona', color: 'bg-emerald-500' },
  { value: 'anulowany', label: 'Odwołana', color: 'bg-red-500' },
];

// ══════════════════════════════════
// Auto Estetyka (detailing)
// ══════════════════════════════════
const DETAILING_STATUSES: KanbanColumn[] = [
  { value: 'nowe', label: 'Nowe', color: 'bg-blue-500' },
  { value: 'wycena', label: 'Wycena', color: 'bg-yellow-500' },
  { value: 'oferta_wyslana', label: 'Oferta wysłana', color: 'bg-orange-500' },
  { value: 'umowione', label: 'Umówione', color: 'bg-indigo-500' },
  { value: 'w_realizacji', label: 'W realizacji', color: 'bg-purple-500' },
  { value: 'zakonczone', label: 'Zakończone', color: 'bg-emerald-500' },
  { value: 'utracone', label: 'Utracone', color: 'bg-red-500' },
];

// ══════════════════════════════════
// Czyste Powietrze
// ══════════════════════════════════
const CP_STATUSES: KanbanColumn[] = [
  { value: 'zapytanie', label: 'Zapytanie', color: 'bg-blue-500' },
  { value: 'audyt_energetyczny', label: 'Audyt energetyczny', color: 'bg-yellow-500' },
  { value: 'kompletacja_dok', label: 'Kompletacja dok.', color: 'bg-orange-500' },
  { value: 'wniosek_zlozony', label: 'Wniosek złożony', color: 'bg-indigo-500' },
  { value: 'w_ocenie', label: 'W ocenie', color: 'bg-purple-500' },
  { value: 'zatwierdzony', label: 'Zatwierdźony', color: 'bg-green-500' },
  { value: 'wybrany_wykonawca', label: 'Wybrany wykonawca', color: 'bg-teal-500' },
  { value: 'prace_w_toku', label: 'Prace w toku', color: 'bg-cyan-500' },
  { value: 'odbior', label: 'Odbiór / Inspekcja', color: 'bg-lime-500' },
  { value: 'zakonczony', label: 'Zakończone', color: 'bg-emerald-500' },
  { value: 'odrzućony', label: 'Odrzucony', color: 'bg-red-500' },
];

// ══════════════════════════════════
// Usługi Księgowe
// ══════════════════════════════════
const ACCOUNTING_STATUSES: KanbanColumn[] = [
  { value: 'zapytanie', label: 'Zapytanie', color: 'bg-blue-500' },
  { value: 'analiza_potrzeb', label: 'Analiza potrzeb', color: 'bg-yellow-500' },
  { value: 'oferta_wyslana', label: 'Oferta wysłana', color: 'bg-cyan-500' },
  { value: 'umowa_podpisana', label: 'Umowa podpisana', color: 'bg-indigo-500' },
  { value: 'aktywna_usluga', label: 'Aktywna usługa', color: 'bg-emerald-500' },
  { value: 'odnowienie', label: 'Odnowienie', color: 'bg-green-500' },
  { value: 'zakonczony', label: 'Zakończona', color: 'bg-red-500' },
];

// ══════════════════════════════════
// Pożyczki Rozwojowe
// ══════════════════════════════════
const DEV_LOAN_STATUSES: KanbanColumn[] = [
  { value: 'zapytanie', label: 'Zapytanie', color: 'bg-blue-500' },
  { value: 'kompletacja_dok', label: 'Kompletacja dok.', color: 'bg-yellow-500' },
  { value: 'wniosek_zlozony', label: 'Wniosek złożony', color: 'bg-indigo-500' },
  { value: 'w_ocenie', label: 'W ocenie', color: 'bg-purple-500' },
  { value: 'zatwierdzony', label: 'Zatwierdźony', color: 'bg-green-500' },
  { value: 'umowa_podpisana', label: 'Umowa podpisana', color: 'bg-teal-500' },
  { value: 'realizacja', label: 'Realizacja', color: 'bg-cyan-500' },
  { value: 'zakonczony', label: 'Zakończone', color: 'bg-emerald-500' },
  { value: 'odrzućony', label: 'Odrzucone', color: 'bg-red-500' },
];

// ══════════════════════════════════
// Dotacje na Rozwój (Development Grants)
// ══════════════════════════════════
const DEV_GRANT_STATUSES: KanbanColumn[] = [
  { value: 'zapytanie', label: 'Zapytanie', color: 'bg-blue-500' },
  { value: 'analiza_kwalifikowalnosci', label: 'Analiza kwalifikowalności', color: 'bg-yellow-500' },
  { value: 'kompletacja_dok', label: 'Kompletacja dok.', color: 'bg-orange-500' },
  { value: 'przygotowanie_wniosku', label: 'Przygotowanie wniosku', color: 'bg-purple-500' },
  { value: 'wniosek_zlozony', label: 'Wniosek złożony', color: 'bg-indigo-500' },
  { value: 'w_ocenie', label: 'W ocenie', color: 'bg-cyan-500' },
  { value: 'zatwierdzony', label: 'Zatwierdźony', color: 'bg-green-500' },
  { value: 'umowa_dofinansowania', label: 'Umowa o dofinansowanie', color: 'bg-teal-500' },
  { value: 'realizacja_projektu', label: 'Realizacja projektu', color: 'bg-lime-500' },
  { value: 'rozliczenie', label: 'Rozliczenie', color: 'bg-emerald-600' },
  { value: 'zakonczony', label: 'Zakończony', color: 'bg-gray-500' },
  { value: 'odrzućony', label: 'Odrzucony', color: 'bg-red-500' },
];

// Statuses after which client can see generated documents (listy intencyjne, umowy)
// Earlier statuses = free analysis / document upload only
export const DOCUMENT_VISIBLE_STATUSES = [
  'umowa_dofinansowania', 'realizacja_projektu', 'rozliczenie', 'zakonczony',
  // PUP grant equivalents
  'podpisana_umowa', 'rozpoczeta_dzialalnosc', 'rozliczony', 'sukces',
];

// ══════════════════════════════════
// TERMINOLOGY (per-product labels)
// ══════════════════════════════════
export const DEFAULT_TERMINOLOGY: NonNullable<ProgramConfig['terminology']> = {
  singular: 'wniosek', plural: 'wniosków', newItem: 'Nowy wniosek',
  pipeline: 'Pipeline — konwersja etapów', winLabel: 'Win rate',
  archiveEmpty: 'Brak zarchiwizowanych wniosków',
  archiveHint: 'Wnioski trafiają tu po zakończeniu (końcowy status + poprzedni rok)',
  yearLabel: 'nabór',
};

const TERM_REZERWACJA: ProgramConfig['terminology'] = {
  singular: 'rezerwacja', plural: 'rezerwacji', newItem: 'Nowa rezerwacja',
  pipeline: 'Pipeline — etapy obsługi', winLabel: 'Zrealizowane',
  archiveEmpty: 'Brak zarchiwizowanych rezerwacji',
  archiveHint: 'Rezerwacje trafiają tu po zakończeniu (końcowy status + poprzedni sezon)',
  yearLabel: 'sezon',
};

const TERM_TURNUS: ProgramConfig['terminology'] = {
  singular: 'turnus', plural: 'turnusów', newItem: 'Nowy turnus',
  pipeline: 'Pipeline — etapy realizacji', winLabel: 'Zrealizowane',
  archiveEmpty: 'Brak zarchiwizowanych turnusów',
  archiveHint: 'Turnusy trafiają tu po zakończeniu (końcowy status + poprzedni sezon)',
  yearLabel: 'sezon',
};

const TERM_ZLECENIE_DETAILING: ProgramConfig['terminology'] = {
  singular: 'zlecenie', plural: 'zleceń', newItem: 'Nowe zlecenie',
  pipeline: 'Pipeline — obsługa zleceń', winLabel: 'Zakończone',
  archiveEmpty: 'Brak zarchiwizowanych zleceń',
  archiveHint: 'Zlecenia trafiają tu po zakończeniu (końcowy status + poprzedni rok)',
  yearLabel: 'rok',
};

const TERM_SZKOLENIE: ProgramConfig['terminology'] = {
  singular: 'szkolenie', plural: 'szkoleń', newItem: 'Nowe szkolenie',
  pipeline: 'Pipeline — etapy', winLabel: 'Zrealizowane',
  archiveEmpty: 'Brak zarchiwizowanych szkoleń',
  archiveHint: 'Szkolenia trafiają tu po zakończeniu (końcowy status + poprzedni rok)',
  yearLabel: 'nabór',
};

const TERM_ZLECENIE: ProgramConfig['terminology'] = {
  singular: 'zlecenie', plural: 'zleceń', newItem: 'Nowe zlecenie',
  pipeline: 'Pipeline — etapy', winLabel: 'Zrealizowane',
  archiveEmpty: 'Brak zarchiwizowanych zleceń',
  archiveHint: 'Zlecenia trafiają tu po zakończeniu (końcowy status + poprzedni rok)',
  yearLabel: 'rok',
};

const TERM_PROJEKT: ProgramConfig['terminology'] = {
  singular: 'projekt', plural: 'projektów', newItem: 'Nowy projekt',
  pipeline: 'Pipeline — etapy realizacji', winLabel: 'Sukces',
  archiveEmpty: 'Brak zarchiwizowanych projektów',
  archiveHint: 'Projekty trafiają tu po zakończeniu (końcowy status + poprzedni rok)',
  yearLabel: 'nabór',
};

const TERM_ZGLOSZENIE: ProgramConfig['terminology'] = {
  singular: 'zgłoszenie', plural: 'zgłoszeń', newItem: 'Nowe zgłoszenie',
  pipeline: 'Pipeline — etapy', winLabel: 'Aktywne',
  archiveEmpty: 'Brak zarchiwizowanych zgłoszeń',
  archiveHint: 'Zgłoszenia trafiają tu po zakończeniu (końcowy status + poprzedni rok)',
  yearLabel: 'rok szkolny',
};

// ══════════════════════════════════
// PROGRAM REGISTRY
// ══════════════════════════════════
export const PROGRAMS: ProgramConfig[] = [
  {
    id: 'pup_grant',
    serviceType: 'pup_grant',
    label: 'CRM Dotacje',
    shortLabel: 'Dotacje',
    icon: Users,
    color: 'blue',
    gradient: 'from-blue-600 to-indigo-600',
    routeBase: '/crm',
    applyRoute: '/crm/apply',
    kanbanStatusField: 'status',
    kanbanColumns: PUP_STATUSES,
    features: {
      ai: true,
      documents: true,
      contractAcceptance: true,
      clientPortal: true,
      meetings: true,
      pointsSystem: true,
      certificates: true,
      courses: true,
      purchaseList: true,
      loanInfo: false,
      repaymentSchedule: false,
      businessPlan: false,
      operatorApplication: false,
    },
    enabled: true,
    terminology: DEFAULT_TERMINOLOGY,
  },
  {
    id: 'wsparcie',
    serviceType: 'wsparcie_na_starcie',
    label: 'CRM Pożyczki',
    shortLabel: 'Pożyczki',
    icon: Banknote,
    color: 'emerald',
    gradient: 'from-emerald-600 to-teal-600',
    routeBase: '/wsparcie',
    applyRoute: '/wsparcie/apply',
    kanbanStatusField: 'loan_status',
    kanbanColumns: LOAN_STATUSES,
    features: {
      ai: true,
      documents: true,
      contractAcceptance: true,
      clientPortal: true,
      meetings: true,
      pointsSystem: false,
      certificates: false,
      courses: false,
      purchaseList: true,
      loanInfo: true,
      repaymentSchedule: true,
      businessPlan: true,
      operatorApplication: true,
    },
    enabled: true,
    terminology: DEFAULT_TERMINOLOGY,
  },
  {
    id: 'kfs',
    serviceType: 'kfs',
    label: 'CRM KFS',
    shortLabel: 'KFS',
    icon: GraduationCap,
    color: 'amber',
    gradient: 'from-amber-600 to-orange-600',
    routeBase: '/kfs',
    applyRoute: '/kfs/apply',
    kanbanStatusField: 'status',
    kanbanColumns: KFS_STATUSES,
    features: {
      ai: true,
      documents: true,
      contractAcceptance: false,
      clientPortal: true,
      meetings: true,
      pointsSystem: false,
      certificates: true,
      courses: true,
      purchaseList: false,
      loanInfo: false,
      repaymentSchedule: false,
      businessPlan: false,
      operatorApplication: false,
    },
    enabled: true,
    terminology: DEFAULT_TERMINOLOGY,
  },
  {
    id: 'krs',
    serviceType: 'krs',
    label: 'CRM KRS',
    shortLabel: 'KRS',
    icon: Building2,
    color: 'violet',
    gradient: 'from-violet-600 to-purple-600',
    routeBase: '/krs',
    applyRoute: '/krs/apply',
    kanbanStatusField: 'krs_status',
    kanbanColumns: KRS_STATUSES,
    features: {
      ai: true,
      documents: true,
      contractAcceptance: true,
      clientPortal: false,
      meetings: true,
      pointsSystem: false,
      certificates: false,
      courses: false,
      purchaseList: false,
      loanInfo: false,
      repaymentSchedule: false,
      businessPlan: false,
      operatorApplication: false,
    },
    enabled: false, // future
    terminology: TERM_ZLECENIE,
  },
  {
    id: 'zus',
    serviceType: 'zus',
    label: 'Ulgi ZUS',
    shortLabel: 'ZUS',
    icon: Shield,
    color: 'slate',
    gradient: 'from-slate-600 to-gray-700',
    routeBase: '/zus',
    applyRoute: '/zus/apply',
    kanbanStatusField: 'status',
    kanbanColumns: ZUS_STATUSES,
    features: {
      ai: true, documents: true, contractAcceptance: false, clientPortal: true,
      meetings: true, pointsSystem: false, certificates: false, courses: false,
      purchaseList: false, loanInfo: false, repaymentSchedule: false,
      businessPlan: false, operatorApplication: false,
    },
    enabled: true,
    terminology: DEFAULT_TERMINOLOGY,
  },
  {
    id: 'bhp',
    serviceType: 'bhp',
    label: 'Szkolenia BHP',
    shortLabel: 'BHP',
    icon: Shield,
    color: 'orange',
    gradient: 'from-orange-600 to-red-600',
    routeBase: '/bhp',
    applyRoute: '/bhp/apply',
    kanbanStatusField: 'status',
    kanbanColumns: BHP_STATUSES,
    features: {
      ai: true, documents: true, contractAcceptance: true, clientPortal: true,
      meetings: true, pointsSystem: false, certificates: true, courses: true,
      purchaseList: false, loanInfo: false, repaymentSchedule: false,
      businessPlan: false, operatorApplication: false,
    },
    enabled: true,
    terminology: TERM_SZKOLENIE,
  },
  {
    id: 'szkolenia',
    serviceType: 'szkolenia',
    label: 'Szkolenia',
    shortLabel: 'Szkolenia',
    icon: BookOpen,
    color: 'indigo',
    gradient: 'from-indigo-600 to-violet-600',
    routeBase: '/szkolenia',
    applyRoute: '/szkolenia/apply',
    kanbanStatusField: 'status',
    kanbanColumns: TRAINING_STATUSES,
    features: {
      ai: true, documents: true, contractAcceptance: false, clientPortal: true,
      meetings: true, pointsSystem: false, certificates: true, courses: true,
      purchaseList: false, loanInfo: false, repaymentSchedule: false,
      businessPlan: false, operatorApplication: false,
    },
    enabled: true,
    terminology: TERM_SZKOLENIE,
  },
  {
    id: 'turnusy',
    serviceType: 'turnusy',
    label: 'Turnusy',
    shortLabel: 'Turnusy',
    icon: Bus,
    color: 'orange',
    gradient: 'from-orange-600 to-amber-600',
    routeBase: '/turnusy',
    applyRoute: '/turnusy/apply',
    kanbanStatusField: 'trip_status',
    kanbanColumns: TRIP_STATUSES,
    features: {
      ai: false, documents: true, contractAcceptance: false, clientPortal: false,
      meetings: true, pointsSystem: false, certificates: false, courses: false,
      purchaseList: false, loanInfo: false, repaymentSchedule: false,
      businessPlan: false, operatorApplication: false,
    },
    enabled: true,
    terminology: TERM_TURNUS,
  },
  {
    id: 'wycieczki',
    serviceType: 'wycieczki',
    label: 'Wycieczki',
    shortLabel: 'Wycieczki',
    icon: Compass,
    color: 'teal',
    gradient: 'from-teal-600 to-cyan-600',
    routeBase: '/wycieczki',
    applyRoute: '/wycieczki/apply',
    kanbanStatusField: 'trip_status',
    kanbanColumns: EXCURSION_STATUSES,
    features: {
      ai: false, documents: true, contractAcceptance: true, clientPortal: true,
      meetings: true, pointsSystem: false, certificates: false, courses: false,
      purchaseList: false, loanInfo: false, repaymentSchedule: false,
      businessPlan: false, operatorApplication: false,
    },
    enabled: true,
    terminology: TERM_REZERWACJA,
  },
  {
    id: 'autoestetyka',
    serviceType: 'autoestetyka',
    label: 'Auto Estetyka',
    shortLabel: 'Auto Estetyka',
    icon: Car,
    color: 'zinc',
    gradient: 'from-zinc-700 to-amber-600',
    routeBase: '/autoestetyka',
    applyRoute: '/autoestetyka/apply',
    kanbanStatusField: 'detailing_status',
    kanbanColumns: DETAILING_STATUSES,
    features: {
      ai: false, documents: true, contractAcceptance: false, clientPortal: true,
      meetings: true, pointsSystem: true, certificates: false, courses: false,
      purchaseList: false, loanInfo: false, repaymentSchedule: false,
      businessPlan: false, operatorApplication: false,
    },
    enabled: true,
    terminology: TERM_ZLECENIE_DETAILING,
  },
  {
    id: 'pozyczki_rozwojowe',
    serviceType: 'pozyczki_rozwojowe',
    label: 'Pożyczki Rozwojowe',
    shortLabel: 'Poż. rozwojowe',
    icon: TrendingUp,
    color: 'cyan',
    gradient: 'from-cyan-600 to-blue-700',
    routeBase: '/pozyczki-rozwojowe',
    applyRoute: '/pozyczki-rozwojowe/apply',
    kanbanStatusField: 'loan_status',
    kanbanColumns: DEV_LOAN_STATUSES,
    features: {
      ai: true, documents: true, contractAcceptance: true, clientPortal: true,
      meetings: true, pointsSystem: false, certificates: false, courses: false,
      purchaseList: true, loanInfo: true, repaymentSchedule: true,
      businessPlan: true, operatorApplication: true,
    },
    enabled: true,
    terminology: DEFAULT_TERMINOLOGY,
  },
  {
    id: 'dotacje_rozwojowe',
    serviceType: 'dotacje_rozwojowe',
    label: 'Dotacje na Rozwój',
    shortLabel: 'Dotacje rozw.',
    icon: Rocket,
    color: 'fuchsia',
    gradient: 'from-fuchsia-600 to-pink-600',
    routeBase: '/dotacje-rozwojowe',
    applyRoute: '/dotacje-rozwojowe/apply',
    kanbanStatusField: 'dev_grant_status',
    kanbanColumns: DEV_GRANT_STATUSES,
    features: {
      ai: true, documents: true, contractAcceptance: true, clientPortal: true,
      meetings: true, pointsSystem: false, certificates: false, courses: false,
      purchaseList: true, loanInfo: false, repaymentSchedule: false,
      businessPlan: true, operatorApplication: true,
    },
    enabled: true,
    terminology: DEFAULT_TERMINOLOGY,
  },
  {
    id: 'czyste_powietrze',
    serviceType: 'czyste_powietrze',
    label: 'Czyste Powietrze',
    shortLabel: 'Cz. Powietrze',
    icon: Leaf,
    color: 'green',
    gradient: 'from-green-600 to-lime-600',
    routeBase: '/czyste-powietrze',
    applyRoute: '/czyste-powietrze/apply',
    kanbanStatusField: 'cp_status',
    kanbanColumns: CP_STATUSES,
    features: {
      ai: true, documents: true, contractAcceptance: true, clientPortal: true,
      meetings: true, pointsSystem: false, certificates: false, courses: false,
      purchaseList: true, loanInfo: false, repaymentSchedule: false,
      businessPlan: false, operatorApplication: false,
    },
    enabled: true,
    terminology: DEFAULT_TERMINOLOGY,
  },
  {
    id: 'uslugi_ksiegowe',
    serviceType: 'uslugi_ksiegowe',
    label: 'Usługi Księgowe',
    shortLabel: 'Księgowość',
    icon: Calculator,
    color: 'rose',
    gradient: 'from-rose-600 to-pink-600',
    routeBase: '/ksiegowosc',
    applyRoute: '/ksiegowosc/apply',
    kanbanStatusField: 'accounting_status',
    kanbanColumns: ACCOUNTING_STATUSES,
    features: {
      ai: true, documents: true, contractAcceptance: true, clientPortal: true,
      meetings: true, pointsSystem: false, certificates: false, courses: false,
      purchaseList: false, loanInfo: false, repaymentSchedule: false,
      businessPlan: false, operatorApplication: false,
    },
    enabled: true,
    terminology: TERM_ZLECENIE,
  },
];

// ══════════════════════════════════
// Granty FPN (wewnętrzne projekty grantowe Fundacji)
// ══════════════════════════════════
const GRANT_FPN_STATUSES: KanbanColumn[] = [
  { value: 'pomysl', label: 'Pomysł / Idea', color: 'bg-slate-400' },
  { value: 'badanie', label: 'Badanie programu', color: 'bg-blue-500' },
  { value: 'szkic', label: 'Szkic wniosku', color: 'bg-yellow-500' },
  { value: 'kompletacja', label: 'Kompletacja dokumentów', color: 'bg-orange-500' },
  { value: 'zlozony', label: 'Wniosek złożony', color: 'bg-purple-500' },
  { value: 'w_ocenie', label: 'W ocenie', color: 'bg-cyan-500' },
  { value: 'zatwierdzony', label: 'Zatwierdźony', color: 'bg-green-500' },
  { value: 'realizacja', label: 'W realizacji', color: 'bg-emerald-600' },
  { value: 'rozliczenie', label: 'Rozliczenie', color: 'bg-lime-500' },
  { value: 'zakonczony', label: 'Zakończony', color: 'bg-gray-500' },
  { value: 'odrzućony', label: 'Odrzucony', color: 'bg-red-500' },
];

// ══════════════════════════════════
// Marketing Beauty & Wellness
// ══════════════════════════════════
const MARKETING_STATUSES: KanbanColumn[] = [
  { value: 'zapytanie', label: 'Zapytanie', color: 'bg-blue-500' },
  { value: 'analiza_potrzeb', label: 'Analiza potrzeb', color: 'bg-yellow-500' },
  { value: 'propozycja_strategii', label: 'Propozycja strategii', color: 'bg-purple-500' },
  { value: 'negocjacja', label: 'Negocjacja', color: 'bg-cyan-500' },
  { value: 'umowa_podpisana', label: 'Umowa podpisana', color: 'bg-indigo-500' },
  { value: 'realizacja', label: 'Realizacja', color: 'bg-teal-500' },
  { value: 'zakonczony', label: 'Zakończony', color: 'bg-emerald-500' },
  { value: 'odrzućony', label: 'Odrzucony', color: 'bg-red-500' },
];

// ══════════════════════════════════
// Gastro Consulting HoReCa
// ══════════════════════════════════
const GASTRO_STATUSES: KanbanColumn[] = [
  { value: 'zapytanie', label: 'Zapytanie', color: 'bg-blue-500' },
  { value: 'audyt_wstepny', label: 'Audyt wstępny', color: 'bg-yellow-500' },
  { value: 'rekomendacje', label: 'Rekomendacje', color: 'bg-purple-500' },
  { value: 'wdrozenie', label: 'Wdrożenie', color: 'bg-cyan-500' },
  { value: 'monitorowanie', label: 'Monitorowanie', color: 'bg-teal-500' },
  { value: 'zakonczony', label: 'Zakończony', color: 'bg-emerald-500' },
  { value: 'odrzućony', label: 'Odrzucony', color: 'bg-red-500' },
];

// ══════════════════════════════════
// Hutnik Business Club
// ══════════════════════════════════
const HUTNIK_STATUSES: KanbanColumn[] = [
  { value: 'zapytanie', label: 'Zapytanie', color: 'bg-blue-500' },
  { value: 'kontakt', label: 'Kontakt nawiązany', color: 'bg-yellow-500' },
  { value: 'spotkanie', label: 'Spotkanie', color: 'bg-purple-500' },
  { value: 'negocjacje', label: 'Negocjacje', color: 'bg-cyan-500' },
  { value: 'umowa', label: 'Umowa podpisana', color: 'bg-indigo-500' },
  { value: 'aktywny_sponsor', label: 'Aktywny sponsor', color: 'bg-emerald-500' },
  { value: 'zakonczony', label: 'Zakończony', color: 'bg-gray-500' },
  { value: 'odrzućony', label: 'Odrzucony', color: 'bg-red-500' },
];

// ══════════════════════════════════
// STEAM Academia (Edukacja STEAM w szkołach)
// ══════════════════════════════════
const STEAM_STATUSES: KanbanColumn[] = [
  { value: 'zapytanie', label: 'Zapytanie', color: 'bg-blue-500' },
  { value: 'prezentacja', label: 'Prezentacja / Demo', color: 'bg-yellow-500' },
  { value: 'umowa', label: 'Umowa', color: 'bg-indigo-500' },
  { value: 'onboarding', label: 'Onboarding szkoły', color: 'bg-purple-500' },
  { value: 'aktywny', label: 'Aktywna współpraca', color: 'bg-emerald-500' },
  { value: 'zakonczony', label: 'Zakończony', color: 'bg-gray-500' },
  { value: 'odrzućony', label: 'Odrzucony', color: 'bg-red-500' },
];

// ══════════════════════════════════
// PROGRAM REGISTRY (continued)
// ══════════════════════════════════

// Append additional products to PROGRAMS array
PROGRAMS.push(
  {
    id: 'marketing_beauty',
    serviceType: 'marketing_beauty',
    label: 'Marketing Beauty',
    shortLabel: 'Marketing',
    icon: Megaphone,
    color: 'pink',
    gradient: 'from-pink-600 to-rose-600',
    routeBase: '/marketing-crm',
    applyRoute: '/marketing-crm/apply',
    kanbanStatusField: 'mkt_status',
    kanbanColumns: MARKETING_STATUSES,
    features: {
      ai: true, documents: true, contractAcceptance: true, clientPortal: true,
      meetings: true, pointsSystem: false, certificates: false, courses: false,
      purchaseList: false, loanInfo: false, repaymentSchedule: false,
      businessPlan: false, operatorApplication: false,
    },
    enabled: true,
    terminology: TERM_ZLECENIE,
  },
  {
    id: 'gastro_consulting',
    serviceType: 'gastro_consulting',
    label: 'Gastro Consulting',
    shortLabel: 'Gastro',
    icon: UtensilsCrossed,
    color: 'orange',
    gradient: 'from-orange-600 to-amber-600',
    routeBase: '/gastro',
    applyRoute: '/gastro/apply',
    kanbanStatusField: 'gastro_status',
    kanbanColumns: GASTRO_STATUSES,
    features: {
      ai: true, documents: true, contractAcceptance: true, clientPortal: true,
      meetings: true, pointsSystem: false, certificates: false, courses: false,
      purchaseList: false, loanInfo: false, repaymentSchedule: false,
      businessPlan: true, operatorApplication: false,
    },
    enabled: true,
    terminology: TERM_ZLECENIE,
  },
  {
    id: 'hutnik_biznes',
    serviceType: 'hutnik_biznes',
    label: 'Hutnik Business Club',
    shortLabel: 'Klub Biznesu',
    icon: Briefcase,
    color: 'lime',
    gradient: 'from-lime-600 to-green-600',
    routeBase: '/hutnik-crm',
    applyRoute: '/hutnik-crm/apply',
    kanbanStatusField: 'hutnik_status',
    kanbanColumns: HUTNIK_STATUSES,
    features: {
      ai: true, documents: true, contractAcceptance: true, clientPortal: false,
      meetings: true, pointsSystem: false, certificates: false, courses: false,
      purchaseList: false, loanInfo: false, repaymentSchedule: false,
      businessPlan: false, operatorApplication: false,
    },
    enabled: true,
    terminology: { ...TERM_ZLECENIE, yearLabel: 'sezon' },
  },
  {
    id: 'granty_fpn',
    serviceType: 'granty_fpn',
    label: 'Granty i Projekty FPN',
    shortLabel: 'Granty FPN',
    icon: FolderHeart,
    color: 'amber',
    gradient: 'from-amber-600 to-yellow-600',
    routeBase: '/granty-fpn',
    applyRoute: '/granty-fpn/nowy',
    kanbanStatusField: 'grant_fpn_status',
    kanbanColumns: GRANT_FPN_STATUSES,
    features: {
      ai: true, documents: true, contractAcceptance: false, clientPortal: false,
      meetings: true, pointsSystem: false, certificates: false, courses: false,
      purchaseList: false, loanInfo: false, repaymentSchedule: false,
      businessPlan: false, operatorApplication: false,
    },
    enabled: true,
    terminology: TERM_PROJEKT,
  },
  {
    id: 'steam_education',
    serviceType: 'steam_education',
    label: 'STEAM Academia',
    shortLabel: 'STEAM',
    icon: Atom,
    color: 'purple',
    gradient: 'from-purple-600 to-violet-600',
    routeBase: '/steam-crm',
    applyRoute: '/steam-crm/apply',
    kanbanStatusField: 'steam_status',
    kanbanColumns: STEAM_STATUSES,
    features: {
      ai: true, documents: true, contractAcceptance: true, clientPortal: true,
      meetings: true, pointsSystem: false, certificates: true, courses: true,
      purchaseList: false, loanInfo: false, repaymentSchedule: false,
      businessPlan: false, operatorApplication: false,
    },
    enabled: true,
    terminology: TERM_ZGLOSZENIE,
  },
);

// Helper functions
export const getEnabledPrograms = () => PROGRAMS.filter(p => p.enabled);
export const getProgramById = (id: string) => PROGRAMS.find(p => p.id === id);
export const getProgramByServiceType = (serviceType: string) => PROGRAMS.find(p => p.serviceType === serviceType);
export const getProgramByRoute = (pathname: string) => PROGRAMS.find(p => pathname.startsWith(p.routeBase));

export const getTerminology = (productCode: string) => getProgramByServiceType(productCode)?.terminology || DEFAULT_TERMINOLOGY;

export const isPupProgram = (serviceType?: string) => serviceType === 'pup_grant' || !serviceType;
export const isLoanProgram = (serviceType?: string) => serviceType === 'wsparcie_na_starcie' || serviceType === 'pup_and_loan' || serviceType === 'pozyczki_rozwojowe';
